import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import KPICard from "@/components/common/KPICard";
import PageHeader from "@/components/common/PageHeader";
import DateRangePicker from "@/components/common/DateRangePicker";
import {
  Users, UserCheck, Hourglass, XCircle, Prohibit, ChartLine, CurrencyDollar,
  Coins, Airplane, Wallet, ChartLineUp, Target, TrendUp, Trophy, Clock,
  Warning, CheckSquare, MagnifyingGlass, X, ArrowsClockwise,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, Legend, LineChart, Line,
} from "recharts";
import StatusBadge from "@/components/common/StatusBadge";
import { toast } from "sonner";

const fmt = (n, d = 0) => (n ?? 0).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtUSD = (n) => `$${fmt(n, 2)}`;
const fmtPct = (n) => `${fmt(n, 1)}%`;

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [fundedTrend, setFundedTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContribs, setShowContribs] = useState(false);
  const [contribs, setContribs] = useState(null);
  const [contribsLoading, setContribsLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [range, setRange] = useState({ start: null, end: null, presetId: "all_time", label: "All Time" });

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range.start) params.start_date = range.start;
      if (range.end) params.end_date = range.end;
      const [dash, trend] = await Promise.all([
        api.get("/dashboard", { params }),
        api.get("/dashboard/funded-trend", { params: { days: 30 } }).catch(() => null),
      ]);
      setData(dash.data);
      if (trend?.data) setFundedTrend(trend.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const openContributors = async () => {
    setShowContribs(true);
    if (contribs) return;
    setContribsLoading(true);
    try {
      const res = await api.get("/dashboard/lot-contributors");
      setContribs(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setContribsLoading(false);
    }
  };

  const recomputeAll = async () => {
    setRecomputing(true);
    try {
      const res = await api.post("/admin/recompute-all-participants");
      const r = res.data;
      toast.success(`Recomputed ${r.participants_recomputed} participants · ${r.participants_changed} changed · sum ${r.sum_before} → ${r.sum_after}`);
      // reload dashboard (with current filter) + contributor data
      const params = {};
      if (range.start) params.start_date = range.start;
      if (range.end) params.end_date = range.end;
      const [dash, contribsRes] = await Promise.all([
        api.get("/dashboard", { params }),
        api.get("/dashboard/lot-contributors"),
      ]);
      setData(dash.data);
      setContribs(contribsRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recompute failed");
    } finally {
      setRecomputing(false);
    }
  };

  if (loading) return <div className="p-8 text-gray-400">Loading dashboard...</div>;
  if (!data) return <div className="p-8 text-red-400">Failed to load dashboard</div>;

  const k = data.kpis;
  const c = data.campaign;
  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
  };
  const statusData = Object.entries(data.status_breakdown || {}).map(([k, v]) => ({ name: k.replace(/_/g, " "), count: v }));

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto">
      <PageHeader
        title="Campaign Control Center"
        subtitle={`${c.name} · ${fmtDate(c.start)} → ${fmtDate(c.end)}`}
        breadcrumbs="Dashboard / Overview"
        testId="dashboard-page"
        actions={
          <div className="flex items-center gap-2">
            <DateRangePicker value={range} onChange={setRange} />
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/30">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
              <span className="text-xs font-mono text-blue-300">LIVE</span>
            </div>
          </div>
        }
      />

      {/* Active filter banner */}
      {data.date_range?.active && (
        <div
          data-testid="date-filter-banner"
          className="mb-4 flex items-center justify-between px-4 py-2 rounded-md bg-blue-500/10 border border-blue-500/30 text-xs text-blue-200"
        >
          <span>
            <span className="opacity-70">Filtering by</span>{" "}
            <span className="font-mono font-semibold">{range.label || "Custom"}</span>
            {data.date_range.start && <> · <span className="font-mono">{data.date_range.start}</span></>}
            {data.date_range.end && <> → <span className="font-mono">{data.date_range.end}</span></>}
          </span>
          <button
            onClick={() => setRange({ start: null, end: null, presetId: "all_time", label: "All Time" })}
            className="text-[11px] text-blue-300 hover:text-white px-2 py-0.5 rounded hover:bg-white/5"
            data-testid="clear-filter-banner"
          >
            Clear
          </button>
        </div>
      )}

      {/* Campaign progress strip */}
      <div className="mb-6 bg-[#111827] border border-white/10 rounded-lg p-5">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Campaign progress to grand target</p>
            <p className="font-display text-2xl font-bold text-white">
              <span className="font-mono">{fmt(k.total_valid_lot, 2)}</span>
              <span className="text-gray-500 text-base"> / {fmt(c.target_participants * c.target_lot)} lot</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Net Profit</p>
            <p className={`font-mono text-2xl font-bold ${k.net_profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtUSD(k.net_profit)}</p>
          </div>
        </div>
        <div className="w-full bg-[#1F2937] rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 via-blue-400 to-orange-500 transition-all duration-1000"
            style={{ width: `${Math.min(k.progress_to_target_percent, 100)}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2 font-mono">{fmtPct(k.progress_to_target_percent)} of grand target</p>
      </div>

      {/* Participant funnel KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <KPICard testId="kpi-total" title="Total Participants" value={fmt(k.total_participants)} icon={<Users size={20} weight="duotone" />} accent="blue" />
        <KPICard testId="kpi-approved" title="Approved" value={fmt(k.approved_participants)} icon={<UserCheck size={20} weight="duotone" />} accent="green" />
        <KPICard testId="kpi-pending" title="Pending" value={fmt(k.pending_verification)} icon={<Hourglass size={20} weight="duotone" />} accent="orange" />
        <KPICard testId="kpi-rejected" title="Rejected" value={fmt(k.rejected_participants)} icon={<XCircle size={20} weight="duotone" />} accent="red" />
        <KPICard testId="kpi-disqualified" title="Disqualified" value={fmt(k.disqualified_participants)} icon={<Prohibit size={20} weight="duotone" />} accent="red" />
      </div>

      {/* Section header — Silver (campaign) */}
      <div className="flex items-center gap-3 mb-3 mt-2" data-testid="section-silver-header">
        <div className="w-1 h-6 rounded-full bg-blue-400" />
        <div>
          <p className="text-[10px] uppercase tracking-widest text-blue-400/70 font-mono">Campaign Metrics</p>
          <p className="font-display text-base font-bold text-white">Silver / XAG · counts toward milestones</p>
        </div>
      </div>

      {/* Financial KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className="relative">
          <KPICard testId="kpi-valid-lot" title="Total Valid Lot" value={fmt(k.total_valid_lot, 2)} subtitle="Silver / XAG only" icon={<ChartLine size={20} weight="duotone" />} accent="blue" />
          <button
            data-testid="view-contributors-btn"
            onClick={openContributors}
            title="See who contributed"
            className="absolute top-2 right-2 p-1.5 rounded-md bg-white/5 hover:bg-blue-500/20 text-gray-400 hover:text-blue-300 transition-colors"
          >
            <MagnifyingGlass size={14} weight="bold" />
          </button>
        </div>
        <KPICard testId="kpi-revenue" title="Gross Revenue" value={fmtUSD(k.gross_revenue)} subtitle={`${fmtUSD(c.rebate_per_lot)} / lot`} icon={<CurrencyDollar size={20} weight="duotone" />} accent="green" />
        <KPICard testId="kpi-milestone-payout" title="Milestone Payout" value={fmtUSD(k.total_milestone_payout)} icon={<Coins size={20} weight="duotone" />} accent="orange" />
        <KPICard testId="kpi-bali-cost" title="Bali Trip Cost" value={fmtUSD(k.estimated_bali_cost)} subtitle={`${k.bali_qualifiers} qualifier(s)`} icon={<Airplane size={20} weight="duotone" />} accent="orange" />
        <KPICard testId="kpi-campaign-cost" title="Campaign Cost" value={fmtUSD(k.total_campaign_cost)} icon={<Wallet size={20} weight="duotone" />} accent="purple" />
        <KPICard testId="kpi-roi" title="ROI" value={fmtPct(k.roi_percent)} subtitle="Net Profit / Cost" icon={<TrendUp size={20} weight="duotone" />} accent={k.roi_percent >= 0 ? "green" : "red"} />
      </div>

      {/* Operations KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <KPICard testId="kpi-bali-qualifiers" title="Bali Qualifiers" value={fmt(k.bali_qualifiers)} icon={<Trophy size={20} weight="duotone" />} accent="orange" />
        <KPICard testId="kpi-near-milestone" title="Near Next Milestone" value={fmt(k.near_next_milestone)} icon={<Target size={20} weight="duotone" />} accent="blue" />
        <KPICard testId="kpi-active-week" title={data.date_range?.active ? "Active Traders (range)" : "Active Traders / Week"} value={fmt(k.active_traders_week)} icon={<ChartLineUp size={20} weight="duotone" />} accent="green" />
        <KPICard testId="kpi-high-risk" title="High Risk" value={fmt(k.high_risk_participants)} icon={<Warning size={20} weight="duotone" />} accent="red" />
        <KPICard testId="kpi-pending-approvals" title="Pending Approvals" value={fmt(k.pending_reward_approvals)} icon={<CheckSquare size={20} weight="duotone" />} accent="orange" />
        <KPICard testId="kpi-pending-payout" title="Pending Payout $" value={fmtUSD(k.pending_payout_amount)} icon={<Clock size={20} weight="duotone" />} accent="orange" />
      </div>

      {/* Broker balance funded rate */}
      {k.balance_checked_participants > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <KPICard
            testId="kpi-funded"
            title={`Funded ≥ $${k.balance_threshold_usd}`}
            value={`${fmt(k.funded_participants)} / ${fmt(k.balance_checked_participants)}`}
            subtitle={`${((k.funded_participants / Math.max(k.balance_checked_participants, 1)) * 100).toFixed(0)}% funded (broker balance)`}
            icon={<CurrencyDollar size={20} weight="duotone" />}
            accent="green"
          />
        </div>
      )}

      {/* Non-Campaign (behavioral) trading activity */}
      {data.non_campaign_activity && (
        <>
          <div className="flex items-center gap-3 mb-3 mt-2" data-testid="section-non-silver-header">
            <div className="w-1 h-6 rounded-full bg-orange-400" />
            <div>
              <p className="text-[10px] uppercase tracking-widest text-orange-400/70 font-mono">Behavioral Insight</p>
              <p className="font-display text-base font-bold text-white">Non-Silver Trading · does NOT count toward milestones</p>
            </div>
          </div>
          <div className="mb-6 bg-[#111827] border border-white/10 rounded-lg p-5" data-testid="non-campaign-section">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[11px] text-gray-500">What else are participants trading besides the campaign symbols? (Not counted toward milestones.)</p>
            <span className="text-[11px] px-2 py-1 rounded-md bg-white/5 text-gray-400 font-mono">
              Excludes: {(c.campaign_symbol_keywords || ["SILVER","XAG"]).join(", ")}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="p-4 rounded-md border border-orange-500/20 bg-orange-500/5">
              <p className="text-[10px] uppercase tracking-widest text-gray-500">Total Non-Silver Lot</p>
              <p className="font-mono text-2xl font-bold text-orange-300 mt-1">{fmt(data.non_campaign_activity.totals.lot, 2)}</p>
              {k.total_valid_lot > 0 && (
                <p className="text-[10px] text-gray-500 mt-1">
                  Ratio: {(data.non_campaign_activity.totals.lot / Math.max(k.total_valid_lot, 0.001)).toFixed(2)}× silver
                </p>
              )}
            </div>
            <div className="p-4 rounded-md border border-white/10 bg-[#0B0F19]">
              <p className="text-[10px] uppercase tracking-widest text-gray-500">Non-Silver Trades</p>
              <p className="font-mono text-2xl font-bold text-white mt-1">{fmt(data.non_campaign_activity.totals.trades)}</p>
            </div>
            <div className="p-4 rounded-md border border-white/10 bg-[#0B0F19]">
              <p className="text-[10px] uppercase tracking-widest text-gray-500">Traders on Other Pairs</p>
              <p className="font-mono text-2xl font-bold text-white mt-1">{fmt(data.non_campaign_activity.totals.participants)}</p>
              <p className="text-[10px] text-gray-500 mt-1">Distinct participants</p>
            </div>
          </div>

          {data.non_campaign_activity.by_symbol.length === 0 ? (
            <div className="p-6 text-center border border-dashed border-white/10 rounded-md text-sm text-gray-500">
              No non-silver trading activity in this range.
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="non-campaign-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase text-gray-500 border-b border-white/10">
                    <th className="py-2 pr-3">Symbol</th>
                    <th className="py-2 pr-3 text-right">Lot Volume</th>
                    <th className="py-2 pr-3 text-right">Trades</th>
                    <th className="py-2 pr-3 text-right">Traders</th>
                    <th className="py-2 pr-3">Share of Non-Silver</th>
                  </tr>
                </thead>
                <tbody>
                  {data.non_campaign_activity.by_symbol.map((s) => {
                    const share = data.non_campaign_activity.totals.lot > 0
                      ? (s.lot / data.non_campaign_activity.totals.lot) * 100
                      : 0;
                    return (
                      <tr key={s.symbol} data-testid={`row-${s.symbol}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="py-2 pr-3 font-mono text-orange-300">{s.symbol}</td>
                        <td className="py-2 pr-3 text-right font-mono text-white">{fmt(s.lot, 2)}</td>
                        <td className="py-2 pr-3 text-right font-mono text-gray-300">{s.trades}</td>
                        <td className="py-2 pr-3 text-right font-mono text-gray-300">{s.participants}</td>
                        <td className="py-2 pr-3 w-64">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                              <div className="h-full bg-orange-500/70" style={{ width: `${share.toFixed(1)}%` }} />
                            </div>
                            <span className="text-[11px] text-gray-400 font-mono w-12 text-right">{share.toFixed(1)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          </div>
        </>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-[#111827] border border-white/10 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500">Trade Lot Growth</p>
              <p className="font-display text-lg font-bold text-white mt-0.5">Cumulative valid lot · revenue</p>
            </div>
            <span className="text-xs text-gray-500 font-mono">{data.lot_growth.length} days</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.lot_growth}>
              <defs>
                <linearGradient id="lotGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F97316" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#F97316" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="#6B7280" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="left" stroke="#6B7280" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="right" orientation="right" stroke="#6B7280" tick={{ fontSize: 10 }} />
              <Tooltip
                contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#9CA3AF" }}
              />
              <Area yAxisId="left" type="monotone" dataKey="cumulative_lots" name="Cumulative lots" stroke="#2563EB" fill="url(#lotGrad)" strokeWidth={2} />
              <Area yAxisId="right" type="monotone" dataKey="revenue" name="Revenue USD" stroke="#F97316" fill="url(#revGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-[#111827] border border-white/10 rounded-lg p-5">
          <p className="text-xs uppercase tracking-widest text-gray-500">Participant Status</p>
          <p className="font-display text-lg font-bold text-white mt-0.5 mb-4">Distribution</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={statusData} layout="vertical">
              <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
              <XAxis type="number" stroke="#6B7280" tick={{ fontSize: 10 }} />
              <YAxis dataKey="name" type="category" stroke="#9CA3AF" tick={{ fontSize: 11 }} width={120} />
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="count" fill="#2563EB" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Funded participants trend */}
      {fundedTrend && fundedTrend.points?.length > 0 && (
        <div data-testid="funded-trend-chart" className="bg-[#111827] border border-white/10 rounded-lg p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500">Broker balance trend</p>
              <p className="font-display text-lg font-bold text-white mt-0.5">
                Funded participants (≥ ${fundedTrend.threshold_usd})
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500 font-mono">{fundedTrend.points.length} days</p>
              {(() => {
                const pts = fundedTrend.points.filter(p => p.recorded);
                if (pts.length < 2) return null;
                const first = pts[0].funded_count;
                const last = pts[pts.length - 1].funded_count;
                const delta = last - first;
                const pct = first > 0 ? ((delta / first) * 100).toFixed(0) : (last > 0 ? "∞" : "0");
                const color = delta > 0 ? "text-emerald-400" : delta < 0 ? "text-red-400" : "text-gray-400";
                return (
                  <p className={`text-xs font-mono ${color}`}>
                    {delta > 0 ? "▲" : delta < 0 ? "▼" : "="} {Math.abs(delta)} ({pct}%)
                  </p>
                );
              })()}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={fundedTrend.points.map(p => ({ ...p, dateShort: p.date.slice(5) }))}>
              <defs>
                <linearGradient id="fundedLine" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="dateShort" stroke="#6B7280" tick={{ fontSize: 10 }} />
              <YAxis stroke="#6B7280" tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#9CA3AF" }}
                formatter={(value, name) => name === "funded_count" ? [`${value} funded`, "Above threshold"] : [`${value}`, "Checked"]}
                labelFormatter={(label, payload) => {
                  const p = payload?.[0]?.payload;
                  return p?.date + (p?.recorded ? "" : " (carried forward)");
                }}
              />
              <Line type="monotone" dataKey="funded_count" name="funded_count" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3, fill: "#10B981" }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="total_checked" name="total_checked" stroke="#6B7280" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-[11px] text-gray-500 mt-2 flex items-center gap-4">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Funded (broker balance ≥ ${fundedTrend.threshold_usd})</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-0.5 border-t border-dashed border-gray-500" /> Total checked</span>
          </p>
        </div>
      )}

      {/* Status breakdown badges */}
      <div className="bg-[#111827] border border-white/10 rounded-lg p-5">
        <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">Status & risk breakdown</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(data.status_breakdown || {}).map(([s, c]) => (
            <div key={s} className="flex items-center gap-2">
              <StatusBadge status={s} />
              <span className="font-mono text-sm text-white">{c}</span>
            </div>
          ))}
          <div className="border-l border-white/10 mx-3" />
          {Object.entries(data.risk_breakdown || {}).map(([s, c]) => (
            <div key={s} className="flex items-center gap-2">
              <StatusBadge status={s} />
              <span className="font-mono text-sm text-white">{c}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Lot Contributors Modal */}
      {showContribs && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
          onClick={() => setShowContribs(false)}
          data-testid="contributors-modal"
        >
          <div
            className="relative bg-[#0B0F19] border border-white/10 rounded-lg w-full max-w-4xl mt-10 mb-10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#0B0F19] rounded-t-lg z-10">
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500">Diagnostic</p>
                <p className="font-display text-lg font-bold text-white">Lot Contributors</p>
                {contribs && (
                  <p className="text-[11px] text-gray-500 font-mono mt-1">
                    Symbols counted: {contribs.campaign_symbol_keywords?.join(", ") || "(none configured)"} · Total {fmt(contribs.total_valid_lot, 2)} lot
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowContribs(false)}
                data-testid="close-contributors"
                className="text-gray-400 hover:text-white p-2 rounded-md hover:bg-white/5"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-6 pt-3 pb-2 flex items-center justify-end border-b border-white/5">
              <button
                data-testid="recompute-all-btn"
                onClick={recomputeAll}
                disabled={recomputing}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium bg-orange-500/10 border border-orange-500/30 text-orange-300 hover:bg-orange-500/20 disabled:opacity-50"
                title="Re-evaluate every participant's total_valid_lot using CURRENT campaign symbol keywords. Use this after changing symbol settings or if the Leaderboard is stale."
              >
                <ArrowsClockwise size={14} className={recomputing ? "animate-spin" : ""} />
                {recomputing ? "Recomputing…" : "Recompute All Participants"}
              </button>
            </div>
            <div className="p-6">
              {contribsLoading && <p className="text-gray-400 text-sm">Loading…</p>}
              {!contribsLoading && contribs && contribs.contributors.length === 0 && (
                <div className="p-6 text-center border border-dashed border-white/10 rounded-md">
                  <p className="text-sm text-gray-400 mb-2">No contributors match the current campaign symbol filter.</p>
                  <p className="text-xs text-gray-500">
                    If Dashboard shows a lot total but this list is empty, check that trades were tagged
                    with a symbol matching your keywords ({contribs.campaign_symbol_keywords?.join(", ") || "none"}).
                    You may need to re-run XM sync or update campaign settings.
                  </p>
                </div>
              )}
              {!contribsLoading && contribs && contribs.contributors.length > 0 && (
                <>
                  <p className="text-xs uppercase text-gray-500 tracking-widest mb-2">Participants</p>
                  <div className="overflow-x-auto mb-6">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase text-gray-500 border-b border-white/10">
                          <th className="py-2 pr-3">Participant</th>
                          <th className="py-2 pr-3">Broker Acc</th>
                          <th className="py-2 pr-3">Status</th>
                          <th className="py-2 pr-3 text-right">Lot</th>
                          <th className="py-2 pr-3 text-right">Trades</th>
                          <th className="py-2 pr-3">Last Close</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contribs.contributors.map((c) => (
                          <tr key={c.participant_id} className="border-b border-white/5">
                            <td className="py-2 pr-3 text-white">{c.full_name || <span className="text-gray-500">—</span>}</td>
                            <td className="py-2 pr-3 font-mono text-xs text-gray-300">{c.broker_account_number}</td>
                            <td className="py-2 pr-3 text-xs text-gray-400">{c.status}</td>
                            <td className="py-2 pr-3 text-right font-mono text-blue-300">{fmt(c.lot, 2)}</td>
                            <td className="py-2 pr-3 text-right font-mono text-gray-300">{c.trade_count}</td>
                            <td className="py-2 pr-3 text-[11px] text-gray-500 whitespace-nowrap">{c.last_close_time?.slice(0, 16).replace("T", " ") || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs uppercase text-gray-500 tracking-widest mb-2">Recent Trades ({contribs.recent_trades.length})</p>
                  <div className="overflow-x-auto max-h-96 border border-white/5 rounded-md">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-[#0B0F19]">
                        <tr className="text-left text-[10px] uppercase text-gray-500 border-b border-white/10">
                          <th className="py-2 px-3">Close Time</th>
                          <th className="py-2 px-3">Ticket</th>
                          <th className="py-2 px-3">Participant</th>
                          <th className="py-2 px-3">Symbol</th>
                          <th className="py-2 px-3 text-right">Lot</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contribs.recent_trades.map((t) => (
                          <tr key={t.id} className="border-b border-white/5">
                            <td className="py-1.5 px-3 font-mono text-gray-400 whitespace-nowrap">{t.close_time?.slice(0, 16).replace("T", " ")}</td>
                            <td className="py-1.5 px-3 font-mono text-gray-300">{t.trade_ticket}</td>
                            <td className="py-1.5 px-3 text-gray-300">{t.full_name || t.broker_account_number}</td>
                            <td className="py-1.5 px-3 font-mono text-blue-300">{t.symbol}</td>
                            <td className="py-1.5 px-3 text-right font-mono text-white">{fmt(t.lot_volume, 2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
